import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  ENDPOINTS,
  type ContractType,
  type FailedPipelineItem,
  type HeldPipelineItem,
  type PendingPipelineItem,
  type PipelineStatusResponse,
  type ProcessingStatus,
  type RefusedPipelineItem,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { rerunPipelineContract } from '../api/pipeline-rerun';
import { fetchPipelineStatus } from '../api/pipeline-status';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { TagInput } from '../components/TagInput';
import { PROCESSING_LABELS } from '../components/trust-labels';
import { requireSurface } from '../routes/surfaces';
import { PipelineRunsSection } from './pipeline-runs/PipelineRunsSection';
import { ClauseMiningDryRunsSection } from './clause-mining-dry-runs/ClauseMiningDryRunsSection';
import { CHIPS, CHIP_LABELS, PIPELINE_FORBIDDEN_COPY, chipOf, type StageChip } from './pipeline-status-chips';
import { PanelProblem, Tile } from './quality/panel';
import { usePanel } from './quality/panel-state';

/**
 * Admin **Pipeline** — CLM-70 (D-70-1…D-70-5; R-70-1, D-71-0).
 *
 * Where every contract sits in intake → convert → pseudonymise → extract →
 * verify, and which are held, refused or failed — each with the audit row's
 * reason verbatim (CR-01) and a link to what fixes it. One read,
 * `GET /v1/admin/pipeline/status`, scoped on the API side: a non-admin who
 * lands here sees *their* scope's view (D-70-1), never a 403.
 *
 * ## Runs (CLM-83, R-83-1)
 *
 * The **Runs** section at the top (`pipeline-runs/PipelineRunsSection`) is where
 * routine extraction runs are requested, approved, started and scheduled, with
 * a recorded history (closes B-35). The `run-pipeline` GitHub workflow remains
 * as break-glass only. When an active run finishes, that section calls this
 * one's `reload` so the tiles refresh with what the run wrote.
 *
 * ## Clause mining (CLM-103)
 *
 * The **Clause mining** section just below Runs
 * (`clause-mining-dry-runs/ClauseMiningDryRunsSection`) is the same
 * request/approve/run-now/history mechanism, restricted to **dry runs** of
 * mine-clauses' segment/cluster/describe stages and match-clauses' match
 * stage — no write, no model call, no cost. It does not call this page's
 * `reload`: a dry run never touches `clm.contract`. A live mine-clauses or
 * match-clauses run stays on its own GitHub workflow (see that section's own
 * note for why).
 *
 * ## Re-run (CLM-79, completes R-70-1; widened by CLM-80 D-80-2)
 *
 * A row with `is_rerunnable` carries a **Re-run** control in the Fix cell. The
 * API projects it for `ExtractionFailed` when the latest AI audit row is absent
 * (it failed at the conversion gate or in pseudonymisation, before any model
 * call — the held `unknown` bucket) **or** an `AIExtractionFailure` (a provider
 * or request failure after the boundary — a non-retryable row in the failed
 * list). A refused row is never re-runnable (rule 6). Confirm posts
 * `rerun-pipeline-contract`, which resets the row so the next `run-pipeline`
 * dispatch converts it again; then the whole section re-fetches (no row is
 * patched in place). The button dims for an identity without the contributor
 * role (`mayAttempt` — usability only; the API is the gate, NFR-06; a sign-in
 * that names no role is never disabled, D-82-1). A 409 / 404
 * renders the API's `field_errors` message on the row, so "the model refused —
 * human review" is readable without a reload. The flag is the API's; this page
 * never decides re-runnability from the bucket a row sits in.
 *
 * ## The stage filter is display grouping, never a scope decision
 *
 * Tiles and chips group rows the API already returned within scope. Only the
 * `contract_type` select re-queries.
 *
 * ## Pending is the queue, shown under the Pending tile
 *
 * The three issue lists carry only contracts with a problem, so the Pending
 * tile's figure used to have no rows behind it: a healthy queued contract was
 * counted and never listed. The API now returns `pending` — every
 * `PendingExtraction` contract in scope, in the runner's own pick order — and
 * this page renders it as a **Pending** table when the Pending tile or chip is
 * selected. It is not a fourth issue list: no reason, no fix, nothing wrong.
 * Under "All" and every other chip the table is not shown, so the default view
 * stays the issue view.
 */

const ENDPOINT_PATH = ENDPOINTS['pipeline-status'].path;
const PANEL = 'Pipeline';

/**
 * The display label of each tile — the shared pipeline labels (`trust-labels.ts`).
 * CLM-108 (R-108-14): the `Verified` tile reads **"Settled"**, because an
 * auto-approved contract reaches the end of the pipeline too, and nobody read
 * every one of its fields. Trust is not this page's axis.
 */
const TILE_LABELS: Readonly<Record<ProcessingStatus, string>> = PROCESSING_LABELS;

const TILE_ORDER: readonly ProcessingStatus[] = [
  'PendingExtraction',
  'Converted',
  'Pseudonymised',
  'Extracted',
  'PendingVerification',
  'Verified',
  'ExtractionFailed',
  'manual_entry_pending',
];

function tone(status: ProcessingStatus, n: number): 'bad' | 'warn' | undefined {
  if (n === 0) return undefined;
  if (status === 'ExtractionFailed') return 'bad';
  if (status === 'manual_entry_pending' || status === 'PendingExtraction') return 'warn';
  return undefined;
}

type Item = HeldPipelineItem | RefusedPipelineItem | FailedPipelineItem;

function contractCell(row: Item): ReactNode {
  return (
    <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>
      <code>{row.contract_key}</code>
    </Link>
  );
}

function reasonCell(row: Item): ReactNode {
  if (row.reason_text === null && row.is_rerunnable) {
    /* CLM-79 — a conversion-gate failure writes no audit row at all; the run log is where the reason lives (B-35). */
    return <span className="mut">no recorded reason — see the run log</span>;
  }
  return row.reason_text === null ? (
    <span className="mut">No reason recorded.</span>
  ) : (
    <code className="qerr" title={row.reason_text}>
      {row.reason_text}
    </code>
  );
}

const BASE_COLUMNS: readonly DataTableColumn<Item>[] = [
  { id: 'contract', header: 'Contract', cell: contractCell },
  { id: 'client', header: 'Client', cell: (r) => r.primary_party_display_name ?? '—' },
  { id: 'sow', header: 'SOW', cell: (r) => r.sow_number ?? '—' },
  { id: 'stage', header: 'Stage', cell: (r) => r.stage },
  { id: 'recorded', header: 'Recorded', cell: (r) => <time dateTime={r.recorded_utc}>{r.recorded_utc}</time> },
  { id: 'reason', header: 'Reason', cell: reasonCell },
];

/* -------------------------------------------------------------------------- */
/* Re-run (CLM-79)                                                             */
/* -------------------------------------------------------------------------- */

interface RerunProblem {
  readonly kind: ApiProblemKind;
  readonly field_errors: readonly { readonly path: string; readonly message: string }[];
  readonly correlation_id: string | null;
}

function toRerunProblem(error: unknown): RerunProblem {
  const body = error instanceof ApiError ? (error.body as { field_errors?: unknown } | null) : null;
  const fieldErrors = Array.isArray(body?.field_errors)
    ? (body.field_errors as { path?: unknown; message?: unknown }[])
        .filter((entry) => typeof entry.path === 'string' && typeof entry.message === 'string')
        .map((entry) => ({ path: entry.path as string, message: entry.message as string }))
    : [];
  return {
    kind: classifyApiError(error),
    field_errors: fieldErrors,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

function rerunProblemHead(kind: ApiProblemKind): string {
  switch (kind) {
    case 'conflict':
      return 'The API refused the re-run — the row is not in the re-runnable state';
    case 'not_found':
      return 'This contract is no longer visible to you — reload';
    case 'forbidden':
      return 'Not authorised — contributor role required';
    default:
      return 'The re-run request failed';
  }
}

/**
 * The Re-run control for one held row (CLM-79). Owns its own dialog state so
 * the column definition stays a plain cell; `onDone` is the section's
 * `reload()` — the whole panel re-fetches, no row is patched in place.
 */
/** The roles the gateway grants `pipeline:rerun` to — a display hint, never the gate. */
const RERUN_ROLES: readonly string[] = ['contributor', 'admin'];

function RerunControl({ row, onDone }: { readonly row: Item; readonly onDone: () => void }) {
  const { identity } = useIdentity();
  const mayRerun = mayAttempt(identity, RERUN_ROLES);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<RerunProblem | null>(null);

  const confirm = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const trimmed = reason.trim();
      await rerunPipelineContract({ contract_key: row.contract_key, ...(trimmed === '' ? {} : { reason: trimmed }) });
      setOpen(false);
      setReason('');
      onDone();
    } catch (error) {
      setProblem(toRerunProblem(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {' '}
      <button
        type="button"
        className="btn sm"
        disabled={!mayRerun || busy}
        title={mayRerun ? undefined : 'Contributor role required'}
        aria-label={`Re-run ${row.contract_key}`}
        onClick={() => {
          setOpen((current) => !current);
          setProblem(null);
        }}
      >
        Re-run
      </button>
      {open ? (
        <div className="vform" role="group" aria-label={`Confirm re-run of ${row.contract_key}`}>
          <p>
            <strong>Re-run</strong> — resets this contract so the next pipeline run converts it again
          </p>
          <div className="field">
            <label htmlFor={`rerun-reason-${row.contract_key}`}>Reason (optional)</label>
            <textarea
              id={`rerun-reason-${row.contract_key}`}
              aria-label={`Reason for re-running ${row.contract_key}`}
              value={reason}
              disabled={busy}
              maxLength={1000}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <div className="vacts">
            <button type="button" className="btn pri" disabled={busy} onClick={() => void confirm()}>
              Confirm re-run
            </button>
            <button
              type="button"
              className="btn qt"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setProblem(null);
              }}
            >
              Cancel
            </button>
          </div>
          <p className="mut">
            This resets the row only; the next run — requested in the Runs section above — converts it again.
          </p>
        </div>
      ) : null}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{rerunProblemHead(problem.kind)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
          {problem.correlation_id === null ? null : (
            <p className="mut">
              Correlation <code>{problem.correlation_id}</code>
            </p>
          )}
          <button type="button" className="btn" onClick={onDone}>
            Reload
          </button>
        </div>
      )}
    </>
  );
}

/** The fix, next to the issue (R-70-1). `onRerun` is the section's `reload()`, for the CLM-79 control. */
function heldFix(row: HeldPipelineItem, onRerun: () => void): ReactNode {
  /* CLM-79 — appended to the existing chip, never replacing it; wherever the API says the row is re-runnable. */
  const rerun = row.is_rerunnable ? <RerunControl row={row} onDone={onRerun} /> : null;
  switch (row.held_reason) {
    case 'no_confirmed_alias':
      return (
        <>
          <span className="tag ai">Held</span> <Link to="/governance/party-aliases">Confirm the alias on Party aliases</Link>
          {rerun}
        </>
      );
    case 'processing_restricted':
      return (
        <>
          <span className="tag acc">Withheld — §4C, manual entry</span>
          {rerun}
        </>
      );
    case 'sow_number_unconfirmed':
      return (
        <>
          <span className="tag ai">Held — SOW number unconfirmed</span>{' '}
          <Link to="/admin/ingest">Correct it on Bulk ingest</Link>
        </>
      );
    case 'unknown':
      return (
        <>
          <span className="tag out">Held — no audit row explains it</span>
          {rerun}
        </>
      );
    case 'extraction_failed':
      return <span className="tag bad">Extraction failed</span>;
    default: {
      const exhaustive: never = row.held_reason;
      return exhaustive;
    }
  }
}

function heldColumns(onRerun: () => void): readonly DataTableColumn<HeldPipelineItem>[] {
  return [
    ...(BASE_COLUMNS as readonly DataTableColumn<HeldPipelineItem>[]),
    { id: 'fix', header: 'Fix', cell: (row) => heldFix(row, onRerun) },
  ];
}

/**
 * The failed list's Fix cell (D-80-2). A retryable failure needs nothing from a
 * person. A non-retryable one is `ExtractionFailed` with an `AIExtractionFailure`
 * as its latest AI row — a provider or request failure, not a refusal — and the
 * API marks it `is_rerunnable`, so the Re-run control renders beside the record
 * link. The audit row stays as evidence; only the contract row is reset.
 */
function failedFix(row: FailedPipelineItem, onRerun: () => void): ReactNode {
  if (row.is_retryable) return <span className="tag acc">Retryable — the next run resumes at pseudonymised</span>;
  return (
    <>
      <span className="tag bad">Not retryable</span> <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>Open the record</Link>
      {row.is_rerunnable ? <RerunControl row={row} onDone={onRerun} /> : null}
    </>
  );
}

const REFUSED_COLUMNS: readonly DataTableColumn<RefusedPipelineItem>[] = [
  ...(BASE_COLUMNS as readonly DataTableColumn<RefusedPipelineItem>[]),
  { id: 'category', header: 'Category', cell: (r) => r.refusal_category ?? '—' },
  {
    id: 'fix',
    header: 'Fix',
    cell: (r) => (
      <>
        <span className="tag bad">Routed to human review</span>{' '}
        <Link to={`/contracts/record/${encodeURIComponent(r.contract_key)}`}>Open the record</Link>
      </>
    ),
  },
];

function failedColumns(onRerun: () => void): readonly DataTableColumn<FailedPipelineItem>[] {
  return [
    ...(BASE_COLUMNS as readonly DataTableColumn<FailedPipelineItem>[]),
    { id: 'attempts', header: 'Attempts', cell: (r) => r.attempt_count, cellClassName: 'num' },
    { id: 'fix', header: 'Fix', cell: (row) => failedFix(row, onRerun) },
  ];
}

function visible<T extends Item>(rows: readonly T[], chip: StageChip): readonly T[] {
  return chip === 'all' ? rows : rows.filter((row) => chipOf(row.processing_status) === chip);
}

/* -------------------------------------------------------------------------- */
/* Pending — the queue behind the Pending tile                                 */
/* -------------------------------------------------------------------------- */

/**
 * The queue's columns. No Stage, Reason or Fix: a pending contract sits at
 * intake with nothing wrong with it. The order is the API's, which is the
 * runner's pick order, so the first row is what the next run takes first.
 */
const PENDING_COLUMNS: readonly DataTableColumn<PendingPipelineItem>[] = [
  {
    id: 'contract',
    header: 'Contract',
    cell: (r) => (
      <Link to={`/contracts/record/${encodeURIComponent(r.contract_key)}`}>
        <code>{r.contract_key}</code>
      </Link>
    ),
  },
  { id: 'client', header: 'Client', cell: (r) => r.primary_party_display_name ?? '—' },
  /* CLM-110 (R-110-23) — provisional until an administrator confirms it. */
  { id: 'type', header: 'Type', cell: (r) => <ContractTypeTag type={r.contract_type} confirmation={r.contract_type_confirmation} /> },
  { id: 'sow', header: 'SOW', cell: (r) => r.sow_number ?? '—' },
  { id: 'received', header: 'Received', cell: (r) => <time dateTime={r.created_utc}>{r.created_utc}</time> },
  {
    id: 'next',
    header: 'Next',
    cell: () => <span className="tag acc">Awaiting the next run</span>,
  },
];

export function PipelineStatus() {
  const surface = requireSurface('pipeline');
  const [contractType, setContractType] = useState<'all' | ContractType>('all');
  const [chip, setChip] = useState<StageChip>('all');
  /** CLM-77 — the all-of tag filter; narrows all five statements server-side. */
  const [tags, setTags] = useState<readonly string[]>([]);

  const { state, reload } = usePanel<PipelineStatusResponse>(
    (abort) =>
      fetchPipelineStatus(
        {
          ...(contractType === 'all' ? {} : { contract_type: contractType }),
          ...(tags.length === 0 ? {} : { tags: [...tags] }),
        },
        abort,
      ),
    [contractType, tags],
  );
  /* CLM-79 — the Fix cell's Re-run control re-fetches the section through `reload`. */
  const heldColumnsWithRerun = useMemo(() => heldColumns(reload), [reload]);
  const failedColumnsWithRerun = useMemo(() => failedColumns(reload), [reload]);

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="pipeline-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      {/* CLM-83 — request / approve / run now / cancel / history / schedule; reloads the status below when a run finishes. */}
      <PipelineRunsSection onRunFinished={reload} />

      {/* CLM-103 — mine-clauses and match-clauses dry runs, the same request/approve/run-now/history shape
          as the Runs section above, restricted to dry runs (segment/cluster/describe/match). Does not
          reload the status tiles above: a dry run writes nothing to clm.contract. */}
      <ClauseMiningDryRunsSection />

      <section className="card qpanel" aria-labelledby="pipeline-heading">
        <label className="check">
          Contract type
          <select
            aria-label="Contract type"
            value={contractType}
            onChange={(event) => {
              setContractType(event.target.value as 'all' | ContractType);
            }}
          >
            <option value="all">All types</option>
            <option value="MSA">MSA</option>
            <option value="NDA">NDA</option>
            <option value="SOW">SOW</option>
          </select>
        </label>
        <label className="check" htmlFor="pipeline-tags">
          Tags (all of)
        </label>
        <TagInput id="pipeline-tags" mode="filter" value={tags} onChange={setTags} />

        {state.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={PIPELINE_FORBIDDEN_COPY} />
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <div className="totals" role="group" aria-label="Contracts by stage">
              {TILE_ORDER.map((status) => {
                const n = state.response.counts[status];
                const target = chipOf(status);
                return (
                  <button
                    key={status}
                    type="button"
                    className="ptile"
                    aria-pressed={chip === target}
                    aria-label={`${TILE_LABELS[status]}: ${String(n)} of ${String(state.response.total_in_scope)} in scope`}
                    onClick={() => {
                      setChip((current) => (current === target ? 'all' : target));
                    }}
                  >
                    <Tile
                      label={TILE_LABELS[status]}
                      value={n}
                      sub={`of ${String(state.response.total_in_scope)} in scope`}
                      tone={tone(status, n)}
                    />
                  </button>
                );
              })}
            </div>

            <div className="chips" role="group" aria-label="Stage filter">
              {CHIPS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="btn sm"
                  aria-pressed={chip === c}
                  onClick={() => {
                    setChip(c);
                  }}
                >
                  {CHIP_LABELS[c]}
                </button>
              ))}
              <span className="mut">Grouping only — every row below is already within your scope.</span>
            </div>

            <div className="pipeline-lists">
              {chip === 'Pending' ? (
                <>
                  <h4>Pending</h4>
                  <p className="mut">
                    Waiting for the next run, in the order it picks them. These have nothing wrong with them; a
                    pending contract the runner will not pick is listed under Held as well.
                  </p>
                  <DataTable
                    caption="Pending contracts"
                    columns={PENDING_COLUMNS}
                    rows={state.response.pending}
                    rowKey={(r) => r.contract_key}
                    empty={<p>Nothing pending — no contract is waiting for its first run.</p>}
                  />
                </>
              ) : null}

              <h4>Held</h4>
              <DataTable
                caption="Held contracts"
                columns={heldColumnsWithRerun}
                rows={visible(state.response.held, chip)}
                rowKey={(r) => r.contract_key}
                empty={<p>Nothing held.</p>}
              />

              <h4>Refused</h4>
              <DataTable
                caption="Refused by the model"
                columns={REFUSED_COLUMNS}
                rows={visible(state.response.refused, chip)}
                rowKey={(r) => r.contract_key}
                empty={<p>Nothing refused.</p>}
              />

              <h4>Failed</h4>
              <DataTable
                caption="Extraction failures"
                columns={failedColumnsWithRerun}
                rows={visible(state.response.failed, chip)}
                rowKey={(r) => r.contract_key}
                empty={<p>Nothing failed.</p>}
              />
            </div>

            <p className="mut">
              Generated <time dateTime={state.response.generated_utc}>{state.response.generated_utc}</time>.
            </p>
          </>
        ) : null}
      </section>
    </>
  );
}
